import dayjs, { type Dayjs } from "dayjs";

import { NAVIGATION_ROUTES } from "../../../utils/NavigationUtils";
import { getAdminNow } from "../../../utils/dateService";

export const SGP_RISK_BETS_LIST_PARAM_KEY = "sgpId";
export const SGP_RISK_BETS_LIST_SPORT_ID_PARAM = "sportId";
export const SGP_RISK_BETS_LIST_LEAGUE_ID_PARAM = "leagueId";
export const SGP_RISK_BETS_LIST_SPORT_NAME_PARAM = "sportName";
export const SGP_RISK_BETS_LIST_LEAGUE_NAME_PARAM = "leagueName";
export const SGP_RISK_BETS_LIST_START_DATE_PARAM = "startDate";
export const SGP_RISK_BETS_LIST_END_DATE_PARAM = "endDate";

export const SGP_RISK_BETS_LIST_ROUTE = `${NAVIGATION_ROUTES.SGP_RISK}/:${SGP_RISK_BETS_LIST_PARAM_KEY}`;

export type SgpRiskDateRangeContext = {
  startDate?: string;
  endDate?: string;
};

export type SgpRiskBetsListRouteContext = SgpRiskDateRangeContext & {
  sportId?: string;
  leagueId?: string;
  sportName?: string;
  leagueName?: string;
};

export type ParsedSgpRiskBetsListRouteContext = {
  sportId: string;
  leagueId: string;
  sportName: string;
  leagueName: string;
  startDate: string;
  endDate: string;
};

export function resolveReportingDatesFromIso(
  startDate?: string,
  endDate?: string,
): [Dayjs, Dayjs] {
  const parsedStart = startDate ? dayjs(startDate) : null;
  const parsedEnd = endDate ? dayjs(endDate) : null;

  if (parsedStart?.isValid() && parsedEnd?.isValid()) {
    return [parsedStart, parsedEnd];
  }

  return [getAdminNow().subtract(6, "day").startOf("day"), getAdminNow().endOf("day")];
}

export function buildSgpRiskPath(context?: SgpRiskDateRangeContext): string {
  const path = NAVIGATION_ROUTES.SGP_RISK;
  const params = new URLSearchParams();

  const startDate = context?.startDate?.trim();
  const endDate = context?.endDate?.trim();

  if (startDate) {
    params.set(SGP_RISK_BETS_LIST_START_DATE_PARAM, startDate);
  }

  if (endDate) {
    params.set(SGP_RISK_BETS_LIST_END_DATE_PARAM, endDate);
  }

  const query = params.toString();
  return query ? `${path}?${query}` : path;
}

export function buildSgpRiskBetsListPath(
  sgpId?: string,
  context?: SgpRiskBetsListRouteContext,
): string {
  if (sgpId == null) return NAVIGATION_ROUTES.SGP_RISK;

  const normalizedId = String(sgpId).trim();
  if (!normalizedId) return NAVIGATION_ROUTES.SGP_RISK;

  const path = `${NAVIGATION_ROUTES.SGP_RISK}/${encodeURIComponent(normalizedId)}`;
  const params = new URLSearchParams();

  const sportId = context?.sportId?.trim();
  const leagueId = context?.leagueId?.trim();
  const sportName = context?.sportName?.trim();
  const leagueName = context?.leagueName?.trim();
  const startDate = context?.startDate?.trim();
  const endDate = context?.endDate?.trim();

  if (sportId) {
    params.set(SGP_RISK_BETS_LIST_SPORT_ID_PARAM, sportId);
  }

  if (leagueId) {
    params.set(SGP_RISK_BETS_LIST_LEAGUE_ID_PARAM, leagueId);
  }

  if (sportName) {
    params.set(SGP_RISK_BETS_LIST_SPORT_NAME_PARAM, sportName);
  }

  if (leagueName) {
    params.set(SGP_RISK_BETS_LIST_LEAGUE_NAME_PARAM, leagueName);
  }

  if (startDate) {
    params.set(SGP_RISK_BETS_LIST_START_DATE_PARAM, startDate);
  }

  if (endDate) {
    params.set(SGP_RISK_BETS_LIST_END_DATE_PARAM, endDate);
  }

  const query = params.toString();
  return query ? `${path}?${query}` : path;
}

export function parseSgpRiskBetsListSearchParams(
  searchParams: URLSearchParams,
): ParsedSgpRiskBetsListRouteContext {
  return {
    sportId: searchParams.get(SGP_RISK_BETS_LIST_SPORT_ID_PARAM)?.trim() ?? "",
    leagueId: searchParams.get(SGP_RISK_BETS_LIST_LEAGUE_ID_PARAM)?.trim() ?? "",
    sportName: searchParams.get(SGP_RISK_BETS_LIST_SPORT_NAME_PARAM)?.trim() ?? "",
    leagueName: searchParams.get(SGP_RISK_BETS_LIST_LEAGUE_NAME_PARAM)?.trim() ?? "",
    startDate: searchParams.get(SGP_RISK_BETS_LIST_START_DATE_PARAM)?.trim() ?? "",
    endDate: searchParams.get(SGP_RISK_BETS_LIST_END_DATE_PARAM)?.trim() ?? "",
  };
}
